import type { ComputedRef } from "vue";

import { computed } from "vue";

import { useGlobal } from "@solvari/utils";

import type { PageAttributes } from "@/core/attributes.ts";
import type { AbTestFeatureReader } from "@/core/reader.ts";
import type { AbTestFeatureName } from "@/registry/featureRegistry.ts";

import { getSentryAbTesting } from "@/client/sentry.ts";

const abTestingKey = "abTesting";

function setAbTestFeatureReader(reader: AbTestFeatureReader): void {
  useGlobal<AbTestFeatureReader>(abTestingKey).set(reader);
}

// No reader (uninitialised client, or a request the layer skipped) → defaultValue.
function getAbTestFeature<Type>(
  key: AbTestFeatureName,
  defaultValue: Type | null = null,
): ComputedRef<Type | null> {
  return computed(() => {
    try {
      return (
        (useGlobal<AbTestFeatureReader>(abTestingKey)
          .tryGet()
          ?.getFeatureValue(key, null) as Type | null) ?? defaultValue
      );
    } catch (error) {
      // Decorative AB feature must never take the page down — reported, not swallowed.
      getSentryAbTesting().captureException(error);
      return defaultValue;
    }
  });
}

function setAbTestAttribute<Key extends keyof PageAttributes>(
  key: Key,
  value: PageAttributes[Key],
) {
  const attributes = globalThis.abTestingAttributes;
  if (attributes) {
    attributes[key] = value;
  }
}

export {
  abTestingKey,
  getAbTestFeature,
  setAbTestAttribute,
  setAbTestFeatureReader,
};
